import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { supabase, describeError, type Row, type Update } from '@/lib/supabase'

export type Settings = Row<'settings'>

export const settingsKey = ['settings'] as const

/**
 * The single settings row.
 *
 * Read constantly — the quote builder needs the VAT rate, every document needs
 * the banking block, the scheduler needs the pause switch — and written rarely,
 * so it is cached hard and invalidated explicitly on save.
 */
export function useSettings() {
  return useQuery({
    queryKey: settingsKey,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<Settings> => {
      const { data, error } = await supabase.from('settings').select('*').eq('id', true).single()
      if (error) throw error
      return data
    },
  })
}

export function useUpdateSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (patch: Update<'settings'>): Promise<Settings> => {
      const { data, error } = await supabase.from('settings').update(patch).eq('id', true).select().single()
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(settingsKey, data)
    },
    onError: (error) => {
      toast.error('Could not save', { description: describeError(error) })
    },
  })
}

/**
 * True when nobody has saved the settings screen yet, so the defaults from the
 * migration are still standing in for real business details. Those defaults are
 * plausible enough to be dangerous — a quote would print with a guessed booking
 * fee — so the app says so plainly until she has been through it once.
 */
export function isUnconfigured(settings: Settings | undefined): boolean {
  if (!settings) return false
  return settings.created_at === settings.updated_at
}
